import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Card, CardContent, TextField, Button, Typography,
  Box, Grid, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Stepper, Step, StepLabel, Chip,
  Checkbox, FormControlLabel
} from '@mui/material';
import { School, CalendarToday } from '@mui/icons-material';
import PhoneInput from '../components/PhoneInput';
import axios from 'axios';
import Cookies from 'js-cookie';

const AlumniRegistrationPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingFormOptions, setLoadingFormOptions] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Form Options from Firebase
  const [formOptions, setFormOptions] = useState(null);
  const [currentEra, setCurrentEra] = useState(null);
  const [availableOrganizationalUnits, setAvailableOrganizationalUnits] = useState([]);
  const [availableDegrees, setAvailableDegrees] = useState([]);
  const [availableEmphases, setAvailableEmphases] = useState([]);

  const [formData, setFormData] = useState({
    'First_Name': '',
    'Last_Name': '',
    'Email Contact': '',
    'Password': '',
    'Contact Number': '',
    'graduation': '', // Year comes FIRST
    'Course Level': '',
    'School': '', // Will be Faculty or School depending on era
    'Degree Obtained': '',
    'otherDegreeText': '', // For "Other" degree entry
    'Emphasis': '',
    'otherEmphasisText': '', // For "Other" emphasis entry
    'Gender': '',
    'Country': 'Trinidad and Tobago',
    'Honors': '',
    'Student Id': '',
    'studentIdUnknown': false, // Track if they don't remember Student ID
    'Department': '',
    'USC Email': '',
    'Address': '',
    'Alternative Phone Number': '',
    'Other Degree Attained': '',
    'Current Job Title': '',
    'Attending': '',
    'Share With Employers': false  // Privacy setting - default to private
  });

  // Generate years from 1927 to current year
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: currentYear - 1926 }, (_, i) => currentYear - i);

  const GENDERS = ["Male", "Female"];
  const HONORS = ["", "Cum Laude", "Magna Cum Laude", "Summa Cum Laude"];

  // Course levels - different for pre-1985 vs post-1985
  const COURSE_LEVELS_PRE_1985 = ["Certificate", "Diploma", "Associate Degree", "Bachelor's"];
  const COURSE_LEVELS_POST_1985 = ["Certificate", "Diploma", "Associate Degree", "Bachelor's", "Undergraduate", "Graduate"];

  // Determine which course levels to show based on current era
  const getCourseLevels = () => {
    if (currentEra === 'pre1985') {
      return COURSE_LEVELS_PRE_1985;
    }
    return COURSE_LEVELS_POST_1985;
  };

  // Fetch form options from Firebase on component mount
  useEffect(() => {
    fetchFormOptions();
  }, []);

  const fetchFormOptions = async () => {
    setLoadingFormOptions(true);
    setError('');

    console.log('[FETCH] Starting form options fetch...');

    try {
      const token = Cookies.get('idToken');
      console.log('[FETCH] Token present:', !!token);

      const response = await axios.get(
        `${API_BASE_URL}/admin/form-options`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      console.log('[FETCH] Response received:', response.status);
      console.log('[FETCH] Response data keys:', Object.keys(response.data || {}));
      console.log('[FETCH] Full response data:', response.data);

      // Check if programsByEra exists and log its structure
      if (response.data && response.data.programsByEra) {
        console.log('[PROGRAMS BY ERA] ✅ Found! Available eras:', Object.keys(response.data.programsByEra));
        Object.keys(response.data.programsByEra).forEach(era => {
          const eraData = response.data.programsByEra[era];
          const orgType = eraData.organizationType;
          const unitsKey = orgType === 'Faculties' ? 'faculties' : 'schools';
          const units = eraData[unitsKey];
          console.log(`[ERA: ${era}] Type: ${orgType}, Units:`, units ? Object.keys(units) : 'NONE');
        });
      } else {
        console.error('[NO PROGRAMS BY ERA] ❌ programsByEra not found in response');
        console.error('[RESPONSE DATA]', response.data);
      }

      if (!response.data) {
        console.error('[FETCH] ❌ No data in response!');
        setError('Failed to load form configuration - no data received');
      } else {
        setFormOptions(response.data);
        console.log('[FETCH] ✅ Form options set successfully');
      }
    } catch (err) {
      console.error('[FETCH] ❌ Error fetching form options:', err);
      console.error('[FETCH] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError('Failed to load form configuration: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingFormOptions(false);
      console.log('[FETCH] Loading complete');
    }
  };

  // Determine era based on graduation year
  const determineEra = (year) => {
    const yearNum = parseInt(year);
    if (yearNum < 1985) return 'pre1985';
    if (yearNum >= 1985 && yearNum < 2010) return '1985to2009';
    return '2010onwards';
  };

  // Get era display info
  const getEraInfo = (eraKey) => {
    if (!formOptions?.programsByEra?.[eraKey]) return null;
    return formOptions.programsByEra[eraKey];
  };

  // Handle year change - triggers era detection
  const handleYearChange = (event) => {
    const year = event.target.value;
    setFormData(prev => ({
      ...prev,
      'graduation': year,
      'Course Level': '', // Reset course level when year changes
      'School': '', // Reset selections
      'Degree Obtained': '',
      'Emphasis': '',
      'otherDegreeText': '',
      'otherEmphasisText': ''
    }));

    if (year) {
      const era = determineEra(year);
      console.log('[YEAR CHANGE] Year:', year, '→ Era:', era);
      setCurrentEra(era);
      const eraData = formOptions?.programsByEra?.[era];

      console.log('[ERA DATA]', eraData);

      if (eraData) {
        const orgType = eraData.organizationType;

        // Pre-1985: No faculties/schools, degrees are at top level
        if (orgType === 'None' || era === 'pre1985') {
          console.log('[PRE-1985] No organizational units, degrees at top level');
          setAvailableOrganizationalUnits([]); // No faculties/schools
          // Set degrees directly for pre-1985
          if (eraData.degrees) {
            let degreeKeys = Object.keys(eraData.degrees);
            // Move "Other" to the end if it exists
            if (degreeKeys.includes('Other')) {
              degreeKeys = degreeKeys.filter(d => d !== 'Other');
              degreeKeys.push('Other');
            }
            console.log('[PRE-1985 DEGREES]', degreeKeys);
            setAvailableDegrees(degreeKeys);
          }
        } else {
          // 1985-2009 or 2010+: Has faculties or schools
          const units = eraData.faculties || eraData.schools || {};
          console.log('[UNITS]', 'Type:', orgType, 'Count:', Object.keys(units).length, 'Units:', Object.keys(units));
          setAvailableOrganizationalUnits(Object.keys(units));
          setAvailableDegrees([]); // Reset degrees until faculty/school selected
        }
      } else {
        console.warn('[NO ERA DATA] Era key not found in formOptions.programsByEra');
      }
    } else {
      setCurrentEra(null);
      setAvailableOrganizationalUnits([]);
      setAvailableDegrees([]);
    }
  };

  // Handle organizational unit (Faculty/School) change
  const handleOrganizationalUnitChange = (event) => {
    const unit = event.target.value;
    setFormData(prev => ({
      ...prev,
      'School': unit,
      'Degree Obtained': '',
      'Emphasis': '',
      'otherDegreeText': '',
      'otherEmphasisText': ''
    }));

    if (unit && currentEra) {
      const eraData = formOptions.programsByEra[currentEra];
      const units = eraData.faculties || eraData.schools;
      const degrees = units[unit]?.degrees || {};
      let degreeKeys = Object.keys(degrees);
      // Move "Other" to the end if it exists
      if (degreeKeys.includes('Other')) {
        degreeKeys = degreeKeys.filter(d => d !== 'Other');
        degreeKeys.push('Other');
      }
      setAvailableDegrees(degreeKeys);
    } else {
      setAvailableDegrees([]);
    }
  };

  // Handle degree change
  const handleDegreeChange = (event) => {
    const degree = event.target.value;
    setFormData(prev => ({
      ...prev,
      'Degree Obtained': degree,
      'Emphasis': '',
      'otherDegreeText': degree === 'Other' ? prev.otherDegreeText : '',
      'otherEmphasisText': ''
    }));

    if (degree && degree !== 'Other' && currentEra && formOptions?.programsByEra?.[currentEra]) {
      const eraData = formOptions.programsByEra[currentEra];
      let emphases = {};

      // Pre-1985: degrees are at top level
      if (eraData.organizationType === 'None' || currentEra === 'pre1985') {
        emphases = eraData.degrees?.[degree]?.emphases || {};
      } else {
        // 1985+ : degrees are under faculties/schools
        if (formData.School) {
          const units = eraData.faculties || eraData.schools;
          emphases = units?.[formData.School]?.degrees?.[degree]?.emphases || {};
        }
      }

      // Get emphasis keys and always ensure "None" and "Other" are available
      let emphasisKeys = Object.keys(emphases);

      // Always add "None" if not present
      if (!emphasisKeys.includes('None')) {
        emphasisKeys.unshift('None');
      }

      // Always add "Other" if not present
      if (!emphasisKeys.includes('Other')) {
        emphasisKeys.push('Other');
      }

      setAvailableEmphases(emphasisKeys);
    } else {
      setAvailableEmphases([]);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Clear "Other" emphasis text when changing away from "Other"
      ...(field === 'Emphasis' && value !== 'Other' ? { otherEmphasisText: '' } : {})
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    const checked = event.target.checked;
    setFormData(prev => ({
      ...prev,
      [field]: checked,
      // Clear Student ID when "Don't Remember" is checked
      ...(field === 'studentIdUnknown' && checked ? { 'Student Id': '' } : {})
    }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

  const handleNext = () => {
    if (activeStep === 1) {
      const email = formData['Email Contact'];
      if (email && !isValidEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      setEmailError('');
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData['Degree Obtained'] === 'Other' && !formData.otherDegreeText?.trim()) {
      setError('Please specify your qualification when selecting "Other"');
      setLoading(false);
      return;
    }

    if (formData['Emphasis'] === 'Other' && !formData.otherEmphasisText?.trim()) {
      setError('Please specify your emphasis when selecting "Other"');
      setLoading(false);
      return;
    }

    try {
      const token = Cookies.get('idToken');

      // Prepare submission data
      const submitData = { ...formData };

      // Handle "Other" selections
      if (submitData['Degree Obtained'] === 'Other') {
        submitData['Degree Obtained'] = submitData.otherDegreeText;
      }
      if (submitData['Emphasis'] === 'Other') {
        submitData['Emphasis'] = submitData.otherEmphasisText;
      }

      // Remove helper fields
      delete submitData.otherDegreeText;
      delete submitData.otherEmphasisText;
      delete submitData.studentIdUnknown;

      // PRE-1985 FIX: Set default School value since no faculty/school structure existed
      if (currentEra === 'pre1985' && (!submitData['School'] || submitData['School'].trim() === '')) {
        submitData['School'] = 'No Faculty'; // Default value for pre-1985
        console.log('[SUBMIT] Pre-1985: Setting School to "No Faculty"');
      }

      // Keep fields separate for pending requests so admin can edit them
      // The formatting will happen during approval, not during registration
      console.log('[SUBMIT] Era:', currentEra);
      console.log('[SUBMIT] Keeping fields separate for editing');
      console.log('[SUBMIT] School:', submitData['School']);
      console.log('[SUBMIT] Course Level:', submitData['Course Level']);
      console.log('[SUBMIT] Degree:', submitData['Degree Obtained']);
      console.log('[SUBMIT] Emphasis:', submitData['Emphasis']);

      const response = await axios.post(
        `${API_BASE_URL}/alumni/register`,
        submitData,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      console.log('Registration response:', response.data);
      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Academic History', 'Basic Info', 'Contact Details', 'Privacy Settings', 'Review'];

  if (loadingFormOptions) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading form configuration...</Typography>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', p: 6 }}>
            <School sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Registration Submitted!
            </Typography>
            <Typography variant="body1" paragraph>
              Your alumni registration has been submitted for review.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You will receive an email notification once your registration is approved.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const eraInfo = currentEra ? getEraInfo(currentEra) : null;
  const organizationType = eraInfo?.organizationType || 'Schools';
  const courseLevels = getCourseLevels(); // Get era-appropriate course levels

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Alumni Registration
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            {/* STEP 0: Academic History - YEAR FIRST FLOW */}
            {activeStep === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Academic History
                  </Typography>
                </Grid>

                {/* GRADUATION YEAR - FIRST QUESTION */}
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Graduation Year</InputLabel>
                    <Select
                      value={formData.graduation}
                      onChange={handleYearChange}
                      label="Graduation Year"
                      startAdornment={<CalendarToday sx={{ mr: 1, color: 'action.active' }} />}
                    >
                      {YEARS.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ERA INDICATOR - Show institutional name based on year */}
                {currentEra && eraInfo && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<School />}>
                      <Typography variant="body2" fontWeight="bold">
                        {eraInfo.institutionName}
                      </Typography>
                      <Typography variant="caption">
                        {eraInfo.label} • {eraInfo.yearRange}
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {/* COURSE LEVEL - Shows era-appropriate options */}
                {formData.graduation && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Course Level</InputLabel>
                      <Select
                        value={formData['Course Level']}
                        onChange={handleChange('Course Level')}
                        label="Course Level"
                      >
                        {courseLevels.map((level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* FACULTY/SCHOOL SELECTOR - Label changes based on era */}
                {/* Skip this for pre-1985 (no organizational structure) */}
                {formData.graduation && formData['Course Level'] && currentEra !== 'pre1985' && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>{organizationType === 'Faculties' ? 'Faculty' : 'School'}</InputLabel>
                      <Select
                        value={formData.School}
                        onChange={handleOrganizationalUnitChange}
                        label={organizationType === 'Faculties' ? 'Faculty' : 'School'}
                      >
                        {availableOrganizationalUnits.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {organizationType === 'Faculties'
                        ? 'Select the Faculty you graduated from'
                        : 'Select the School you graduated from'}
                    </Typography>
                  </Grid>
                )}

                {/* QUALIFICATION */}
                {/* For pre-1985: Show after Course Level. For others: Show after School */}
                {formData['Course Level'] && (currentEra === 'pre1985' || formData.School) && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Qualification Obtained</InputLabel>
                      <Select
                        value={formData['Degree Obtained']}
                        onChange={handleDegreeChange}
                        label="Qualification Obtained"
                      >
                        {availableDegrees.map((degree) => (
                          <MenuItem key={degree} value={degree}>
                            {degree}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* OTHER QUALIFICATION TEXT FIELD */}
                {formData['Degree Obtained'] === 'Other' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Please specify your qualification"
                      value={formData.otherDegreeText}
                      onChange={handleChange('otherDegreeText')}
                      placeholder="e.g., B.S. Environmental Science"
                    />
                  </Grid>
                )}

                {/* EMPHASIS */}
                {formData['Degree Obtained'] && formData['Degree Obtained'] !== 'Other' && availableEmphases.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Emphasis (Optional)</InputLabel>
                      <Select
                        value={formData.Emphasis}
                        onChange={handleChange('Emphasis')}
                        label="Emphasis (Optional)"
                      >
                        {availableEmphases.map((emphasis) => (
                          <MenuItem key={emphasis} value={emphasis}>
                            {emphasis}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* OTHER EMPHASIS TEXT FIELD */}
                {formData.Emphasis === 'Other' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Please specify your emphasis"
                      value={formData.otherEmphasisText}
                      onChange={handleChange('otherEmphasisText')}
                      placeholder="e.g., International Business"
                    />
                  </Grid>
                )}

                {/* HONORS */}
                {formData['Degree Obtained'] && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Honors</InputLabel>
                      <Select
                        value={formData.Honors}
                        onChange={handleChange('Honors')}
                        label="Honors"
                      >
                        <MenuItem value="">None</MenuItem>
                        {HONORS.filter(h => h !== '').map((honor) => (
                          <MenuItem key={honor} value={honor}>
                            {honor}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* STUDENT ID */}
                {formData['Degree Obtained'] && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Student ID (if known)"
                      value={formData['Student Id']}
                      onChange={handleChange('Student Id')}
                      disabled={formData.studentIdUnknown}
                      autoComplete="off"
                      inputProps={{ autoComplete: 'off' }}
                      helperText={formData.studentIdUnknown ? "You marked that you don't remember your Student ID" : ""}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.studentIdUnknown}
                          onChange={handleCheckboxChange('studentIdUnknown')}
                          size="small"
                        />
                      }
                      label="Don't Remember / None"
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                )}
              </Grid>
            )}

            {/* STEP 1: Basic Info */}
            {activeStep === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Personal Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    value={formData.First_Name}
                    onChange={handleChange('First_Name')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    value={formData.Last_Name}
                    onChange={handleChange('Last_Name')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.Gender}
                      onChange={handleChange('Gender')}
                      label="Gender"
                    >
                      {GENDERS.map((gender) => (
                        <MenuItem key={gender} value={gender}>
                          {gender}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Email"
                    type="email"
                    value={formData['Email Contact']}
                    onChange={(e) => {
                      handleChange('Email Contact')(e);
                      if (emailError) setEmailError('');
                    }}
                    onBlur={() => {
                      const email = formData['Email Contact'];
                      if (email && !isValidEmail(email)) {
                        setEmailError('Please enter a valid email address');
                      } else {
                        setEmailError('');
                      }
                    }}
                    error={!!emailError}
                    helperText={emailError || ''}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Password"
                    type="password"
                    value={formData.Password}
                    onChange={handleChange('Password')}
                    helperText="For accessing your alumni account"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    required
                    label="Contact Number"
                    value={formData['Contact Number']}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, 'Contact Number': val }))
                    }
                  />
                </Grid>
              </Grid>
            )}

            {/* STEP 2: Contact Details */}
            {activeStep === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="USC Email (if you remember it)"
                    value={formData['USC Email']}
                    onChange={handleChange('USC Email')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.Address}
                    onChange={handleChange('Address')}
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={formData.Country}
                    onChange={handleChange('Country')}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    label="Alternative Phone Number"
                    value={formData['Alternative Phone Number']}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, 'Alternative Phone Number': val }))
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Job Title"
                    value={formData['Current Job Title']}
                    onChange={handleChange('Current Job Title')}
                  />
                </Grid>
              </Grid>
            )}

            {/* STEP 3: Privacy Settings */}
            {activeStep === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Privacy Settings
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Card sx={{ bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.main', p: 2 }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData['Share With Employers']}
                            onChange={(e) => setFormData({
                              ...formData,
                              'Share With Employers': e.target.checked
                            })}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              Share my profile with employers and recruiters
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Allow companies with View Only or Business access to see your
                              graduation year, degree, school, and contact information.
                              You can change this anytime in your profile settings.
                            </Typography>
                          </Box>
                        }
                      />

                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>What's visible when you opt-in:</strong><br/>
                          • Name, Degree, School/Faculty, Graduation Year<br/>
                          • Contact Email and Phone Number<br/>
                          • Current Job Title (if provided)<br/>
                          <br/>
                          <strong>What's always private:</strong><br/>
                          • Student ID, Address, USC Email<br/>
                          • Any other personal details
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* STEP 4: Review */}
            {activeStep === 4 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Review Your Information
                </Typography>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Name:</Typography>
                    <Typography variant="body1">{formData.First_Name} {formData.Last_Name}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Email:</Typography>
                    <Typography variant="body1">{formData['Email Contact']}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Graduation Year:</Typography>
                    <Typography variant="body1">{formData.graduation}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Institution:</Typography>
                    <Typography variant="body1">{eraInfo?.institutionName}</Typography>
                  </Grid>

                  {formData.School && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">{organizationType === 'Faculties' ? 'Faculty' : 'School'}:</Typography>
                      <Typography variant="body1">{formData.School}</Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Qualification:</Typography>
                    <Typography variant="body1">
                      {(() => {
                        // Format degree preview the same way as submission
                        let degreeValue = formData['Degree Obtained'];
                        if (degreeValue === 'Other') {
                          degreeValue = formData.otherDegreeText;
                        }

                        let emphasisValue = formData.Emphasis;
                        if (emphasisValue === 'Other') {
                          emphasisValue = formData.otherEmphasisText;
                        }

                        let formatted = degreeValue;

                        // Only format for pre-1985
                        if (currentEra === 'pre1985' && formData['Course Level'] && degreeValue) {
                          formatted = `${formData['Course Level']} in ${degreeValue}`;
                        }

                        // Add emphasis for all eras
                        if (emphasisValue && emphasisValue !== 'None' && emphasisValue.trim() !== '') {
                          formatted += `, Emphasis in ${emphasisValue}`;
                        }

                        return formatted;
                      })()}
                    </Typography>
                  </Grid>

                  {formData.Honors && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Honors:</Typography>
                      <Typography variant="body1">{formData.Honors}</Typography>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Privacy Setting:</Typography>
                    <Chip
                      label={formData['Share With Employers'] ? 'Profile will be visible to employers' : 'Profile will be private'}
                      color={formData['Share With Employers'] ? 'success' : 'default'}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Back
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit Registration'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    (activeStep === 0 && (
                      !formData.graduation ||
                      !formData['Degree Obtained'] ||
                      // Only require School for post-1985 eras
                      (currentEra !== 'pre1985' && !formData.School)
                    )) ||
                    (activeStep === 1 && (!formData.First_Name || !formData.Last_Name || !formData['Email Contact'] || !!emailError || !isValidEmail(formData['Email Contact'])))
                  }
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AlumniRegistrationPage;